import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * RequestAnimationFrame ID for scroll handling
   * @type {number | null}
   */
  #rafId = null;

  /**
   * Flag to track if scroll update is pending
   * @type {boolean}
   */
  #scrollPending = false;

  /**
   * Media query list used to toggle mobile menu variant
   * @type {MediaQueryList | null}
   */
  #mql = null;

  /**
   * Listener for media query changes
   * @type {(ev: MediaQueryListEvent) => void | undefined}
   */
  #mqlListener;

  /**
   * Track the last applied scroll direction so we only update when it meaningfully changes
   * @type {'none' | 'up' | 'down'}
   */
  #scrollDirection = 'none';

  /**
   * Minimum delta before we consider a scroll direction change meaningful
   * @type {number}
   */
  #directionThreshold = 2;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
    // Recompute logo shift when header size changes
    try {
      this.#updateLogoShift();
    } catch (e) {
      // ignore measurement errors
    }
  });

  // Debounce timer id for resize/measurement (0 means no timer)
  #logoShiftTimer = 0;

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.#setStickyState(!isIntersecting);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Helper to update sticky state dataset and trigger meta theme color updates when needed.
   * @param {boolean} isActive
   */
  #setStickyState(isActive) {
    const next = isActive ? 'active' : 'inactive';

    if (this.dataset.stickyState === next) return;

    this.dataset.stickyState = next;
    try {
      changeMetaThemeColor(this.refs.headerRowTop);
    } catch (e) {
      // Ignore failures updating theme color (e.g. refs not ready yet)
    }
  }

  /**
   * Update scroll direction dataset with debouncing to avoid rapid flicker.
   * @param {'none' | 'up' | 'down'} direction
   */
  #setScrollDirection(direction) {
    if (this.#scrollDirection === direction) return;
    this.dataset.scrollDirection = direction;
    this.#scrollDirection = direction;
  }

  /**
   * Determine and apply the scroll direction, respecting a tiny threshold to smooth micro-scrolls.
   * @param {number} delta
   * @param {boolean} isSticky
   */
  #updateScrollDirection(delta, isSticky) {
    if (!isSticky) {
      this.#setScrollDirection('none');
      return;
    }

    const absDelta = Math.abs(delta);

    if (absDelta > this.#directionThreshold || this.#scrollDirection === 'none') {
      this.#setScrollDirection(delta > 0 ? 'down' : 'up');
    }
  }

  #handleWindowScroll = () => {
    // Debounce using requestAnimationFrame for smooth updates
    if (this.#scrollPending) return;
    
    this.#scrollPending = true;
    
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
    }
    
    this.#rafId = requestAnimationFrame(() => {
      this.#scrollPending = false;
      this.#processScroll();
    });
  };

  #processScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    // Allow handling for both 'always' and 'scroll-up' modes; when other modes
    // are used, only proceed if header has gone offscreen
    if (stickyMode !== 'always' && stickyMode !== 'scroll-up' && !this.#offscreen) return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const delta = scrollTop - this.#lastScrollTop;
    const threshold = 8;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const shouldStick = scrollTop > threshold;
      this.#updateScrollDirection(delta, shouldStick);
      this.#setStickyState(shouldStick);
      this.classList.toggle('header--shrunken', shouldStick);
      this.classList.toggle('header--scrolled', shouldStick);
      this.#offscreen = shouldStick;
    } else if (stickyMode === 'scroll-up') {
      if (scrollTop > threshold) {
        this.#offscreen = true;
        this.#setStickyState(true);
        this.#updateScrollDirection(delta, true);
        this.classList.add('header--shrunken');
        this.classList.add('header--scrolled');
      } else {
        this.#offscreen = false;
        this.#setStickyState(false);
        this.#updateScrollDirection(0, false);
        this.classList.remove('header--shrunken');
        this.classList.remove('header--scrolled');
      }
    } else {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.#offscreen = false;
        this.#setStickyState(false);
        this.#updateScrollDirection(0, false);
        this.classList.remove('header--shrunken');
        this.classList.remove('header--scrolled');
      } else {
        this.#setStickyState(true);
        this.#updateScrollDirection(delta, true);
        this.classList.add('header--shrunken');
        this.classList.add('header--scrolled');
      }
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Measure left / right groups inside the header row and set
   * a CSS variable to nudge the logo for visual centering on mobile.
   * Positive value moves logo to the right.
   */
  #updateLogoShift = () => {
    try {
      const row = this.refs?.headerRowTop || this.querySelector('.header__row');
      const logo = this.querySelector('.header__logo');
      if (!row || !logo) {
        this.style.removeProperty('--header-logo-shift');
        return;
      }

      // Collect direct children of the row to determine which are left/right of the logo
      const children = Array.from(row.children).filter((n) => n instanceof HTMLElement);
      let logoContainerIndex = children.findIndex((el) => el.contains(logo) || el === logo);
      if (logoContainerIndex === -1) {
        // fallback: try to find by class name
        logoContainerIndex = children.findIndex((el) => el.classList && el.classList.contains('header__logo'));
      }

      // If still not found, bail
      if (logoContainerIndex === -1) {
        this.style.removeProperty('--header-logo-shift');
        return;
      }

      const leftChildren = children.slice(0, logoContainerIndex);
      const rightChildren = children.slice(logoContainerIndex + 1);

      /**
       * Sum widths of elements
       * @param {HTMLElement[]} els
       * @returns {number}
       */
      const sumWidth = (els) => els.reduce((acc, el) => {
        try { return acc + el.getBoundingClientRect().width; } catch (e) { return acc; }
      }, 0);

      const leftWidth = sumWidth(leftChildren);
      const rightWidth = sumWidth(rightChildren);

      // Positive shift moves logo to the right when left is wider than right
      const shift = Math.round((leftWidth - rightWidth) / 2);

      // Only set when significant to avoid micro-adjustments
      if (Math.abs(shift) < 1) {
        this.style.removeProperty('--header-logo-shift');
      } else {
        this.style.setProperty('--header-logo-shift', `${shift}px`);
      }
    } catch (e) {
      // defensive: ignore
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.classList.add('header--cloned');

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
        // Apply initial state in case page loads mid-scroll
        this.#handleWindowScroll();
      }
    }

    // Setup mobile menu variant toggle: add `.mobile` on small viewports
    try {
  const bp = parseInt(this.getAttribute('data-mobile-breakpoint') || this.dataset.mobileBreakpoint || '767', 10) || 767;
  const mq = window.matchMedia(`(max-width: ${bp}px)`);
      this.#mql = mq;
      /** @type {(ev: MediaQueryListEvent) => void} */
      const listener = (ev) => this.#updateMobileMenu(ev.matches);
      this.#mqlListener = listener;

      // Older browsers use addListener
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', listener);
      } else if (typeof mq.addListener === 'function') {
        mq.addListener(listener);
      }

      // Initialize based on current viewport
      this.#updateMobileMenu(mq.matches);
    } catch (e) {
      // matchMedia may throw in strange embed contexts; ignore safely
    }

    // Initial measurement for logo centering and on resize (debounced)
    try {
      this.#updateLogoShift();
      this.__logoShiftResizeHandler = () => {
        if (this.#logoShiftTimer) clearTimeout(this.#logoShiftTimer);
        this.#logoShiftTimer = setTimeout(() => this.#updateLogoShift(), 80);
      };
      window.addEventListener('resize', this.__logoShiftResizeHandler);
    } catch (e) {
      // ignore
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');

    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    if (this.#logoShiftTimer) {
      clearTimeout(this.#logoShiftTimer);
      this.#logoShiftTimer = 0;
    }
    this.classList.remove('header--scrolled');
    this.classList.remove('header--shrunken');
    this.classList.remove('header--cloned');
    try {
      if (this.__logoShiftResizeHandler) {
        window.removeEventListener('resize', this.__logoShiftResizeHandler);
        this.__logoShiftResizeHandler = null;
      }
    } catch (e) {
      // ignore
    }

    // Clean up media query listener
    try {
  if (this.#mql && typeof this.#mqlListener !== 'undefined') {
        if (typeof this.#mql.removeEventListener === 'function') {
          this.#mql.removeEventListener('change', this.#mqlListener);
        } else if (typeof this.#mql.removeListener === 'function') {
          this.#mql.removeListener(this.#mqlListener);
        }
      }
    } catch (e) {
      // ignore errors during teardown
    }
  }

  /**
   * Toggle the mobile variant class on the header menu element.
   * @param {boolean} isMobile
   */
  #updateMobileMenu(isMobile) {
    try {
      const menu = this.refs?.headerMenu;
      if (!menu) return;

      if (isMobile) menu.classList.add('mobile');
      else menu.classList.remove('mobile');
    } catch (e) {
      // defensive: ignore any DOM errors
    }
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
